import React, {useEffect, useRef, useState} from "react";
import {observer} from "mobx-react";
import styled, {css, keyframes} from "styled-components";
import {CardBox} from "../../../basic-components/card/card.box";
import {useServices} from "../../../hooks/use-services.hook";
import {useContainerMediaQueriesChecks} from "../../../hooks/use-container-media-queries-checks.hook";
import {
    InsuranceSelectionAgreementEnum
} from "../../../services/booking/models/insurance/providers/insurance-selection-agreement.enum";
import {reaction} from "mobx";
import {delay} from "../../../utils/util-functions";
import {
    INeatPaymentPageSellStrategyViewModel
} from "../../../services/booking/models/insurance/providers/neat/strategies/payment-page/neat-payment-page-sell-strategy.view-model.interface";

const shakeAnimation = keyframes`
    0%   { transform: translate3d(0, 0, 0); }
    15%  { transform: translate3d(-7px, 0, 0); }
    30%  { transform: translate3d(7px, 0, 0); }
    45%  { transform: translate3d(-6px, 0, 0); }
    60%  { transform: translate3d(6px, 0, 0); }
    75%  { transform: translate3d(-5px, 0, 0); }
    90%  { transform: translate3d(5px, 0, 0); }
    100% { transform: translate3d(0, 0, 0); }
`;

const ContainerBox = styled(CardBox)<{$shake: boolean}>`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${props => props.theme.spacing.spacing12};
    width: 100%;
    background-color: ${props => props.theme.colors.successShade};
    color: ${props => props.theme.colors.successContrast};
    ${props => props.$shake && css`
        animation: ${shakeAnimation} 0.4s ease-in-out 3;
    `}
`

const QuotesDetailsAndPriceBox = styled.div`
    display: flex;
    flex-direction: row;
    flex-grow: 1;
    gap: ${props => props.theme.spacing.spacing12};
    ${props => props.theme.containerMediaQuery.smallScreen} {
        flex-direction: column;
    }
`

const QuotesDetailsContainerBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: ${props => props.theme.spacing.spacing12};
    flex-grow: 1;
    ${props => props.theme.containerMediaQuery.smallScreen} {
        gap: ${props => props.theme.spacing.spacing8};
    }
`

const QuotesDetailsBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${props => props.theme.spacing.spacing8};
    flex-grow: 1;
`

const TitleBox = styled.div`
    font-size: ${props => props.theme.fontSize.h4};
    font-weight: ${props => props.theme.fontWeight.semiBold};
`

const SubtitleBox = styled.div`
    font-size: ${props => props.theme.fontSize.h6};
    font-weight: ${props => props.theme.fontWeight.semiBold};
`

const DocumentLinkBox = styled.div`
    white-space: pre;
    font-size: ${props => props.theme.fontSize.body2};
    & a {
        color: ${props => props.theme.colors.darkTint};
    }
`

const QuotesPriceBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    font-size: ${props => props.theme.fontSize.h4};
    font-weight: ${props => props.theme.fontWeight.semiBold};
`

const QuotesButtonsBox = styled.div`
    display: flex;
    flex-direction: row;
    gap: ${props => props.theme.spacing.spacing16};
    ${props => props.theme.containerMediaQuery.smallScreen} {
        flex-direction: column;
        gap: ${props => props.theme.spacing.spacing12};
    } 
`

const ButtonBox = styled.div<{$isSelected: boolean}>`
    padding: ${props => props.theme.spacing.spacing12} ${props => props.theme.spacing.spacing30};
    border: 2px solid ${props => props.theme.colors.successContrast};
    cursor: pointer;
    border-radius: ${props => props.theme.border.defaultRadius};
    
    ${props => props.$isSelected && css`
        background-color: ${props => props.theme.colors.successContrast};
        color: ${props => props.theme.colors.successShade};
    `}
`

const ErrorMessageBox = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    transform: translateY(100%);
    color: ${props => props.theme.colors.danger};
    font-weight: ${props => props.theme.fontWeight.medium};
`


interface NeatPaymentPageQuotesComponentProps {
    neatStrategy: INeatPaymentPageSellStrategyViewModel;
}
export const NeatInsuranceSellInPaymentPageComponent: React.FC<NeatPaymentPageQuotesComponentProps> = observer((props) => {
    const services = useServices();
    const [shake, setShake] = useState(false);
    const containerMediaQuery = useContainerMediaQueriesChecks();
    const containerRef = useRef<HTMLDivElement>(null);
    const neatStrategy = props.neatStrategy;
    const product = neatStrategy.product;

    useEffect(() => {

        props.neatStrategy.attachValidatedDOMElement(containerRef.current);
        const errorReaction = reaction(() => props.neatStrategy.errorMessage,
            async (errMsg) => {
                const shouldShake = Boolean(errMsg);
                if(shouldShake) {
                    await delay(Date.now(), 400); //enough for the scroll into view to finish
                }
                setShake(shouldShake);
            })

        return () => {
            errorReaction();

        }

    }, [props.neatStrategy]);

    const renderIcon = () => {
        if(!product.iconUrl) {
            return null;
        }
        return (
            <img src={product.iconUrl} alt={product.quoteTitle} width={20} height={20}/>
        );
    }

    const renderDocumentLink = () => {
        if(!product.documentLinkHtml) {
            return null;
        }
        return (
            <DocumentLinkBox dangerouslySetInnerHTML={{
                __html: product.documentLinkHtml
            }}/>
        );
    }

    const renderPriceInsideDetails = () => {
        if(containerMediaQuery.smallScreen) {
            return (
                <QuotesPriceBox>
                    {product.priceToDisplay}
                </QuotesPriceBox>
            );
        }
    }

    const renderPriceOutsideDetails = () => {
        if(containerMediaQuery.smallScreen) {
            return null;
        }

        return (
            <QuotesPriceBox>
                {product.priceToDisplay}
            </QuotesPriceBox>
        );
    }

    const onYes = async () => {
        await props.neatStrategy.acceptProduct();
    }

    const onNo = async () => {
        await props.neatStrategy.declineProduct();
    }

    const renderError = () => {
        if(!props.neatStrategy.errorMessage) {
            return null;
        }

        return (
            <ErrorMessageBox>
                {props.neatStrategy.errorMessage}
            </ErrorMessageBox>
        );
    }

    return (
        <ContainerBox ref={containerRef} $shake={shake}>
            <QuotesDetailsAndPriceBox>
                <QuotesDetailsContainerBox>
                    {renderIcon()}
                    <QuotesDetailsBox>
                        <TitleBox>
                            {product.quoteTitle}
                        </TitleBox>
                        <SubtitleBox>
                            {product.quoteSubtitle}
                        </SubtitleBox>
                        {renderDocumentLink()}
                        {renderPriceInsideDetails()}
                    </QuotesDetailsBox>
                </QuotesDetailsContainerBox>
                {renderPriceOutsideDetails()}
            </QuotesDetailsAndPriceBox>
            <QuotesButtonsBox>
                <ButtonBox $isSelected={props.neatStrategy.userAgreement === InsuranceSelectionAgreementEnum.Agree} onClick={onYes}>
                    {services.language.translate('Yes')}
                </ButtonBox>
                <ButtonBox $isSelected={props.neatStrategy.userAgreement === InsuranceSelectionAgreementEnum.Disagree} onClick={onNo}>
                    {services.language.translate('No')}
                </ButtonBox>
            </QuotesButtonsBox>
            {renderError()}
        </ContainerBox>
    )
})